/** @forge-header
 * @id          valueDashboardView
 * @role        Multi-sport value dashboard | thin shell composing extracted hooks + sub-components
 * @pattern     component
 * @tier        T3
 * @purity      dom | localStorage
 * @units       cents | dollars
 * @money-path  yes
 * @fan-out     ~1
 * @upstream    hooks/ui/useGameSlate | hooks/ui/useSportMarketSelection
 *              | hooks/ui/usePolyTokenLabels | hooks/ui/useEffectiveData
 *              | hooks/trading/useTradeModal | hooks/useRotos
 *              | nbaConsolidated/pricing:getTeamWinData,liquidityDollars
 *              | nbaConsolidated/volumeSettings | nbaConsolidated/viewModeSettings
 *              | sportsStream:MoneylineGameData,moneylineToConsolidated
 * @downstream  App.tsx (lazy-loaded page route)
 * @exports     ValueDashboardView:component
 * @test        untested (integration-level)
 * @lifecycle   init (localStorage volume/viewMode) -> sport/market selection -> date filtering
 *              -> game rendering (moneyline|spread|total) -> trade modal submission
 * @invariant   date-snap guard | must not snap while loading — partial availableDates can mis-snap
 * @danger      effectiveSportsGames merges test data by Map key — stale keys cause phantom games
 */

import { useState, useCallback, useMemo, useEffect } from 'react';
import { cn } from '@/lib/infra/utils';
import type { GameData, SportsLoadingState } from '@/lib/sportsStream';
import type { MoneylineGameData } from '@/lib/sportsStream';
import { moneylineToConsolidated } from '@/lib/sportsStream';
import type { ConsolidatedGameBooks } from '@/lib/nbaConsolidated/types';
import { useRotos } from '@/hooks/useRotos';
import type { generateMockTraderState } from '@/mock/trader';
import type { NBAMarketRow, Position } from '@/types';
import type { KalshiOrder } from '@/lib/kalshiApi';
import type { PolyOpenOrder } from '@/lib/polymarket/types';
import type { VelocityResult } from '@/lib/trading/queueVelocity';
import type { Sport, MarketType } from '@/lib/sportsDiscovery';

// Extracted hooks
import { useGameSlate } from '@/hooks/ui/useGameSlate';
import { useSportMarketSelection } from '@/hooks/ui/useSportMarketSelection';
import { useTradeModal } from '@/hooks/trading/useTradeModal';
import { usePolyTokenLabels } from '@/hooks/ui/usePolyTokenLabels';
import { useEffectiveData } from '@/hooks/ui/useEffectiveData';

// Components
import {
  DashboardHeader,
  type OddsMode,
  type ViewMode,
} from '@/components/nba-value-dashboard/DashboardHeader';
import { DashboardToolbar } from '@/components/nba-value-dashboard/DashboardToolbar';
import { GameTableArea } from '@/components/nba-value-dashboard/GameTableArea';
import { LegacyOpenOrdersPanel } from '@/components/nba-value-dashboard/OpenOrdersPanel';
import { LegacyOpenPositionsPanel } from '@/components/nba-value-dashboard/OpenPositionsPanel';
import { LegacyCombinedOrdersPositionsPanel } from '@/components/nba-value-dashboard/CombinedOrdersPositionsPanel';
import { GameAccordion } from '@/components/nba-value-dashboard/GameAccordion';
import { PlaceOrderModal } from '@/components/nba-value-dashboard/PlaceOrderModal';
import { getTeamWinData, liquidityDollars } from '@/lib/nbaConsolidated/pricing';
import {
  type VolumeWindow,
  initVolumeSettings,
  toggleWindow,
} from '@/lib/nbaConsolidated/volumeSettings';
import {
  getViewMode,
  setViewMode as persistViewMode,
} from '@/lib/nbaConsolidated/viewModeSettings';

/** Stable empty defaults to avoid creating new Map() on every render */
const EMPTY_MAP = new Map() as Map<string, never>;
const EMPTY_BOOL_MAP = new Map() as Map<string, boolean>;
const EMPTY_STATE_MAP = new Map() as Map<string, SportsLoadingState>;

interface ValueDashboardViewProps {
  markets?: NBAMarketRow[];
  orders?: KalshiOrder[];
  consolidatedGames?: ConsolidatedGameBooks[];
  consolidatedLoading?: boolean;
  feedStatus?: 'live' | 'offline';
  lastUpdateTime?: string;
  polyOpenOrders?: PolyOpenOrder[];
  polyStatus?: 'disconnected' | 'connected' | 'error';
  velocityByOrderId?: Record<string, VelocityResult>;
  absoluteQueueByOrderId?: Record<string, number>;
  positions?: Position[];
  positionTitleByTicker?: Record<string, string>;
  positionGameDateByTicker?: Record<string, string>;
  positionOutcomeByTicker?: Record<string, { yes?: string; no?: string }>;
  onCancelOrder?: (orderId: string) => Promise<void>;
  onDecreaseOrder?: (orderId: string, nextQuantity: number) => Promise<void> | void;
  onKillAll?: () => void;
  onCancelPolyOrder?: (orderId: string) => void;
  onKillAllPolyOrders?: () => void;
  onPlaceKalshiOrder?: (args: {
    marketTicker: string;
    side: 'yes' | 'no';
    priceCents: number;
    contracts: number;
    isTakerMode: boolean;
    expirationTs?: number;
  }) => Promise<void>;
  onPlacePolymarketOrder?: (args: {
    tokenId: string;
    conditionId?: string;
    action: 'buy' | 'sell';
    priceCents: number;
    contracts: number;
    isTakerMode: boolean;
    tickSize?: string;
    negRisk?: boolean;
  }) => Promise<void>;
  sportsGames?: Map<string, GameData[]>;
  sportsLoading?: Map<string, boolean>;
  sportsLoadingStates?: Map<string, SportsLoadingState>;
  onSportMarketChange?: (sport: Sport, marketType: MarketType) => void;
  feedStale?: boolean;
  testData?: boolean;
  mockTraderState?: ReturnType<typeof generateMockTraderState>;
}

export function ValueDashboardView({
  markets: _markets,
  orders: _orders,
  consolidatedGames = [],
  consolidatedLoading = false,
  feedStatus: _feedStatus = 'offline',
  lastUpdateTime: _lastUpdateTime = '--:--:--',
  polyOpenOrders: _polyOpenOrders = [],
  polyStatus: _polyStatus = 'disconnected',
  velocityByOrderId: _velocityByOrderId,
  absoluteQueueByOrderId: _absoluteQueueByOrderId,
  positions,
  positionTitleByTicker,
  positionGameDateByTicker,
  positionOutcomeByTicker,
  onCancelOrder,
  onDecreaseOrder: _onDecreaseOrder,
  onKillAll: _onKillAll,
  onCancelPolyOrder: _onCancelPolyOrder,
  onKillAllPolyOrders: _onKillAllPolyOrders,
  onPlaceKalshiOrder,
  onPlacePolymarketOrder,
  sportsGames = EMPTY_MAP,
  sportsLoading = EMPTY_BOOL_MAP,
  sportsLoadingStates = EMPTY_STATE_MAP,
  onSportMarketChange,
  feedStale = false,
  testData = false,
  mockTraderState,
}: ValueDashboardViewProps) {
  // ── Extracted hooks ──────────────────────────────────────────────────

  const {
    selectedSport,
    selectedMarketType,
    availableMarketTypes,
    useMoneylineView,
    handleSportChange,
    handleMarketTypeChange,
  } = useSportMarketSelection({ onSportMarketChange });

  // Bridge: derive current sport games from sportsGames map
  const currentGamesKey = `${selectedSport}-${selectedMarketType}`;
  const currentGames = sportsGames.get(currentGamesKey) ?? [];
  const currentLoading = sportsLoading.get(currentGamesKey) ?? false;
  const currentLoadingState = sportsLoadingStates.get(currentGamesKey);

  // Bridge: convert non-NBA moneylines to consolidated format
  const convertedMoneylineGames = useMemo(() => {
    if (selectedSport === 'nba' || selectedMarketType !== 'moneyline') return [];
    return (currentGames as MoneylineGameData[]).map(moneylineToConsolidated);
  }, [currentGames, selectedSport, selectedMarketType]);

  const moneylineGamesRaw = selectedSport === 'nba' ? consolidatedGames : convertedMoneylineGames;
  const moneylineLoading = selectedSport === 'nba' ? consolidatedLoading : currentLoading;
  const moneylineGamesWithRotation = useRotos(selectedSport, moneylineGamesRaw);

  const gameSlate = useGameSlate({
    moneylineGames: moneylineGamesWithRotation,
    currentGames,
    moneylineLoading,
    currentLoading,
    selectedSport,
  });

  const tradeModal = useTradeModal({
    selectedSport,
    moneylineGamesForView: gameSlate.moneylineGamesForView,
    onPlaceKalshiOrder,
    onPlacePolymarketOrder,
  });

  const polyTokenLabels = usePolyTokenLabels({ consolidatedGames, sportsGames });

  const effectiveData = useEffectiveData({
    testData,
    mockTraderState,
    orders: _orders,
    positions,
    positionTitleByTicker,
    positionGameDateByTicker,
    positionOutcomeByTicker,
    consolidatedGames,
    sportsGames,
  });

  // ── Local state ──────────────────────────────────────────────────────

  const [oddsMode, setOddsMode] = useState<OddsMode>('maker');
  const [viewMode, setViewMode] = useState<ViewMode>(() => getViewMode());
  const [expandedGameKeys, setExpandedGameKeys] = useState<Set<string>>(new Set());
  const [volumeWindows, setVolumeWindows] = useState<VolumeWindow[]>([]);
  const [tableCollapsed, setTableCollapsed] = useState(false);
  const [tableCompact, setTableCompact] = useState(false);

  useEffect(() => {
    setVolumeWindows(initVolumeSettings());
  }, []);

  // ── Handlers ─────────────────────────────────────────────────────────

  const handleToggleVolumeWindow = useCallback((window: VolumeWindow) => {
    setVolumeWindows(toggleWindow(window));
  }, []);

  const handleToggleTableCollapsed = useCallback(() => {
    setTableCollapsed((v) => !v);
  }, []);

  const handleToggleTableCompact = useCallback(() => {
    setTableCompact((v) => !v);
  }, []);

  const handleModeChange = useCallback((mode: OddsMode) => {
    setOddsMode(mode);
  }, []);

  const handleViewModeChange = useCallback((mode: ViewMode) => {
    setViewMode(mode);
    persistViewMode(mode);
  }, []);

  const handleCancelOrder = useCallback(
    async (orderId: string) => {
      await onCancelOrder?.(orderId);
    },
    [onCancelOrder]
  );

  const _handleKillAll = useCallback(() => {
    _onKillAll?.();
  }, [_onKillAll]);

  const _handleCancelPolyOrder = useCallback(
    (orderId: string) => {
      _onCancelPolyOrder?.(orderId);
    },
    [_onCancelPolyOrder]
  );

  const _handleKillAllPolyOrders = useCallback(() => {
    _onKillAllPolyOrders?.();
  }, [_onKillAllPolyOrders]);

  const _handleDecreaseOrder = useCallback(
    async (orderId: string, nextQuantity: number) => {
      await _onDecreaseOrder?.(orderId, nextQuantity);
    },
    [_onDecreaseOrder]
  );

  const handleToggleExpand = useCallback((gameKey: string) => {
    setExpandedGameKeys((prev) => {
      if (prev.has(gameKey)) return new Set();
      return new Set([gameKey]);
    });
  }, []);

  // Map market tickers → game keys so odds cell clicks toggle expansion
  const tickerToGameKey = useMemo(() => {
    const map = new Map<string, string>();
    for (const game of gameSlate.moneylineGamesForView) {
      if (game.kalshi) {
        map.set(game.kalshi.markets.away.marketTicker, game.key);
        map.set(game.kalshi.markets.home.marketTicker, game.key);
      }
      if (game.polymarket) {
        const awayToken = game.polymarket.markets.away.tokenId;
        const homeToken = game.polymarket.markets.home.tokenId;
        if (awayToken) map.set(`asset:${awayToken}`, game.key);
        if (homeToken) map.set(`asset:${homeToken}`, game.key);
      }
    }
    return map;
  }, [gameSlate.moneylineGamesForView]);

  const handleNavigateToMarket = useCallback(
    (marketTicker: string) => {
      const gameKey = tickerToGameKey.get(marketTicker);
      if (gameKey) handleToggleExpand(gameKey);
    },
    [tickerToGameKey, handleToggleExpand]
  );

  // ── Derived data ─────────────────────────────────────────────────────

  const maxLiquidity = useMemo(() => {
    if (!useMoneylineView) return 1000;
    const isTakerMode = oddsMode === 'taker';
    let max = 1000;
    for (const g of gameSlate.moneylineGamesForView) {
      const awayK = getTeamWinData({
        game: g.kalshi ?? null,
        venue: 'kalshi',
        teamSide: 'away',
        isTakerMode,
      });
      const homeK = getTeamWinData({
        game: g.kalshi ?? null,
        venue: 'kalshi',
        teamSide: 'home',
        isTakerMode,
      });
      const awayP = getTeamWinData({
        game: g.polymarket ?? null,
        venue: 'polymarket',
        teamSide: 'away',
        isTakerMode,
      });
      const homeP = getTeamWinData({
        game: g.polymarket ?? null,
        venue: 'polymarket',
        teamSide: 'home',
        isTakerMode,
      });
      const a = liquidityDollars(awayK.liq, awayK.priceCents) ?? 0;
      const h = liquidityDollars(homeK.liq, homeK.priceCents) ?? 0;
      const ap = liquidityDollars(awayP.liq, awayP.priceCents) ?? 0;
      const hp = liquidityDollars(homeP.liq, homeP.priceCents) ?? 0;
      max = Math.max(max, a, h, ap, hp);
    }
    return max;
  }, [gameSlate.moneylineGamesForView, oddsMode, useMoneylineView]);

  const _orderbookByTicker = useMemo(() => {
    const result: Record<
      string,
      {
        yesBestBid: number | null;
        yesBestAsk: number | null;
        noBestBid: number | null;
        noBestAsk: number | null;
      }
    > = {};
    for (const game of consolidatedGames) {
      if (game.kalshi) {
        for (const market of [game.kalshi.markets.away, game.kalshi.markets.home]) {
          const yesBestBid = market.yes[0]?.priceCents ?? null;
          const noBestBid = market.no[0]?.priceCents ?? null;
          const yesBestAsk = noBestBid !== null ? 100 - noBestBid : null;
          const noBestAsk = yesBestBid !== null ? 100 - yesBestBid : null;
          result[market.marketTicker] = { yesBestBid, yesBestAsk, noBestBid, noBestAsk };
        }
      }
    }
    return result;
  }, [consolidatedGames]);

  const selectedGameKey = useMemo(() => {
    const keys = Array.from(expandedGameKeys);
    return keys[0] ?? null;
  }, [expandedGameKeys]);

  const selectedGame = useMemo(() => {
    if (!selectedGameKey) return null;
    return gameSlate.moneylineGamesForView.find((g) => g.key === selectedGameKey) ?? null;
  }, [selectedGameKey, gameSlate.moneylineGamesForView]);

  // ── Render ───────────────────────────────────────────────────────────

  return (
    <div className={cn('space-y-6 p-4', feedStale && 'feed-stale')}>
      <DashboardHeader
        oddsMode={oddsMode}
        viewMode={viewMode}
        onModeChange={handleModeChange}
        onViewModeChange={handleViewModeChange}
        volumeWindows={volumeWindows}
        onToggleVolumeWindow={handleToggleVolumeWindow}
        leftContent={
          <DashboardToolbar
            selectedSport={selectedSport}
            selectedMarketType={selectedMarketType}
            availableMarketTypes={availableMarketTypes}
            selectedDate={gameSlate.selectedDate}
            availableDates={gameSlate.availableDates}
            gameFilter={gameSlate.gameFilter}
            onSportChange={handleSportChange}
            onMarketTypeChange={handleMarketTypeChange}
            onDateChange={gameSlate.setSelectedDate}
            onDatePrev={gameSlate.handleDatePrev}
            onDateNext={gameSlate.handleDateNext}
            onDateToday={gameSlate.handleDateToday}
            onDateAll={gameSlate.handleDateAll}
            onGameFilterChange={gameSlate.setGameFilter}
          />
        }
      />

      <GameTableArea
        selectedMarketType={selectedMarketType}
        useMoneylineView={useMoneylineView}
        viewMode={viewMode}
        sport={selectedSport}
        oddsMode={oddsMode}
        moneylineGamesForView={gameSlate.moneylineGamesForView}
        moneylineLoading={moneylineLoading}
        currentLoadingState={currentLoadingState}
        maxLiquidity={maxLiquidity}
        expandedGameKeys={expandedGameKeys}
        onToggleExpand={handleToggleExpand}
        onTradeClick={tradeModal.openTrade}
        onNavigateToMarket={handleNavigateToMarket}
        tableCollapsed={tableCollapsed}
        onToggleCollapsed={handleToggleTableCollapsed}
        tableCompact={tableCompact}
        onToggleCompact={handleToggleTableCompact}
        filteredCurrentGames={gameSlate.filteredCurrentGames}
        currentLoading={currentLoading}
        volumeWindows={volumeWindows}
        selectedGame={selectedGame}
        onCloseDetailPanel={() => setExpandedGameKeys(new Set())}
      />

      <GameAccordion
        orders={effectiveData.effectiveOrders}
        positions={effectiveData.effectivePositions}
        positionTitleByTicker={effectiveData.effectiveTitles}
        positionGameDateByTicker={effectiveData.effectiveDates}
        positionOutcomeByTicker={effectiveData.effectiveOutcomes}
        consolidatedGames={effectiveData.effectiveConsolidated}
        sportsGames={effectiveData.effectiveSportsGames}
        onCancelOrder={handleCancelOrder}
      />

      <LegacyOpenPositionsPanel
        positions={positions ?? []}
        markets={_markets}
        titleByTicker={positionTitleByTicker}
        gameDateByTicker={positionGameDateByTicker}
        outcomeByTicker={positionOutcomeByTicker}
      />
      <LegacyOpenOrdersPanel
        kalshiOrders={_orders ?? []}
        polyOrders={_polyOpenOrders}
        polyStatus={_polyStatus}
        titleByTicker={positionTitleByTicker}
        polyTokenLabels={polyTokenLabels}
        outcomeByTicker={positionOutcomeByTicker}
        velocityByOrderId={_velocityByOrderId}
        absoluteQueueByOrderId={_absoluteQueueByOrderId}
        positions={positions}
        onCancelOrder={handleCancelOrder}
        onDecreaseOrder={_handleDecreaseOrder}
        onKillAll={_handleKillAll}
        onCancelPolyOrder={_handleCancelPolyOrder}
        onKillAllPolyOrders={_handleKillAllPolyOrders}
      />
      <LegacyCombinedOrdersPositionsPanel
        kalshiOrders={_orders ?? []}
        positions={positions ?? []}
        titleByTicker={positionTitleByTicker}
        gameDateByTicker={positionGameDateByTicker}
        outcomeByTicker={positionOutcomeByTicker}
        orderbookByTicker={_orderbookByTicker}
        onCancelOrder={handleCancelOrder}
        onKillAll={_handleKillAll}
      />

      <PlaceOrderModal
        isOpen={tradeModal.isOpen}
        request={tradeModal.placeOrderRequest}
        submitting={tradeModal.tradeSubmitting}
        error={tradeModal.tradeError}
        onClose={tradeModal.closeTrade}
        onSubmit={tradeModal.handleSubmitTrade}
      />
    </div>
  );
}

export default ValueDashboardView;

/** @forge-footer
 * @checklist
 *   1. DATE FILTER | selectedDate null = "all dates" | snap guard skips while loading
 *   2. SPORT SWITCH | market type auto-resets if invalid for new sport
 *   3. TRADE MODAL | tradeReq drives PlaceOrderModal — null = closed
 *   4. TEST DATA | effectiveX merges mock data when testData=true — check Map key collisions
 * @sync-group  none
 * @backlog     F-013: hide completed events (done) | B-001: +1h timezone offset (open)
 */
